import { IconLayer } from '@deck.gl/layers';
import { memoize } from 'lodash';
import { DEVICE_PIXEL_RATIO, type WebGLContext } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import type { LatLng, PoiType } from 'src/lib/player/store';
import { hasMouse } from 'src/lib/player/utils';
import SpriteManager, { type Sprite } from './SpriteManager';

const getPOISpriteManager = memoize(() => new SpriteManager());

export function createPOILayer({
  pois,
  selectedIds,
  ownLocation,
  gl,
  largeMode,
  onClick,
  onHover,
}: {
  pois: Array<PoiType>;
  selectedIds: Set<string>;
  ownLocation: LatLng | null;
  gl: WebGLContext | null;
  largeMode: boolean;
  onClick: (poi: PoiType) => void;
  onHover: (poi: null | PoiType) => void;
}): Array<IconLayer> {
  if (gl == null) return [];
  if (pois.length === 0 && ownLocation == null) return [];

  const spriteManager = getPOISpriteManager();
  const poiSprites: Array<Sprite> = pois.map((poi) => {
    return spriteManager.createPOISprite(poi, largeMode);
  });

  if (ownLocation != null) {
    poiSprites.push(
      spriteManager.createPOISprite(
        {
          type: 'POI',
          id: '___me',
          name: t('Me'),
          latlng: ownLocation,
          index: -1,
          eventIndex: -1,
          markerColor: {
            hex: '#ffffff',
            hexNum: 0xffffff,
            isLight: true,
            rgba: 'rgba(255, 255, 255, 1)',
          },
        },
        largeMode,
      ),
    );
  }

  return spriteManager.updateDirtySpritesheets(gl).map(
    (spritesheet, i) =>
      new IconLayer({
        id: `poi-icons-${i}`,
        iconMapping: spriteManager.sprites,
        iconAtlas: spritesheet.texture,
        data: poiSprites.filter((sprite) => sprite.sheetIndex === i),
        billboard: true,
        pickable: true,
        getSize: (sprite: Sprite) =>
          ((selectedIds.has(sprite._poi?.id ?? '') ? 1 : 0.5) * sprite.height) / DEVICE_PIXEL_RATIO,
        getIcon: (sprite: Sprite) => sprite.hashKey,
        getPosition: (sprite: Sprite, { target }: { target: [number, number, number] }) => {
          target[0] = sprite.lng;
          target[1] = sprite.lat;
          target[2] = 2.0;
          return target;
        },
        getColor: (sprite: Sprite, { target }: { target: [number, number, number, number] }) => {
          target[0] = 255;
          target[1] = 255;
          target[2] = 255;
          target[3] = sprite.alpha * 255;
          return target;
        },
        onClick: ({ object }: { object: Sprite | null }) => {
          const poi = object?._poi;
          if (poi != null && (poi.index ?? -1) >= 0) {
            onClick(poi);
          }
        },
        onHover: hasMouse()
          ? ({ object }: { object: Sprite | null }) => {
              const poi = object?._poi;

              if (object == null) {
                onHover(null);
              } else if (poi != null && (poi.index ?? -1) >= 0) {
                onHover(poi);
              }
            }
          : null,
      }),
  );
}
